import React from 'react';
import { useForm } from 'react-hook-form';

import { Box, Stack, Typography } from '@mui/material';

import { getTaxIdExample, useSnackbar, VALIDATION_RULES } from '@localstack/services';
import { TaxIdTextField, ButtonWithDialog, ContentModalActions } from '@localstack/ui';
import { OrganizationTaxDetails, PatchOrganizationRequest, PatchOrganizationResponse } from '@localstack/types';

interface TaxIdentifierDialogProps {
  country?: string;
  taxDetails: OrganizationTaxDetails;
  /** Handler for when the form is submitted returns whether the submission was successful */
  onSubmit: (taxData: PatchOrganizationRequest) => Promise<PatchOrganizationResponse>;
  /**Whether the org details are currently being updated */
  isUpdatingOrg: boolean;
}

export const TaxIdentifierDialog = ({
  country,
  taxDetails,
  onSubmit,
  isUpdatingOrg,
}: TaxIdentifierDialogProps): JSX.Element => {
  const { showSnackbar } = useSnackbar();

  const { control, handleSubmit, setValue, reset, watch } = useForm<PatchOrganizationRequest>({
    mode: 'all',
    defaultValues: {
      tax_details: taxDetails,
    },
  });

  const taxId = watch('tax_details.tax_id');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFormSubmit = async (data: any, closeDialog: () => void) => {
    const didSubmitSuccessfully = await onSubmit(data);

    if (didSubmitSuccessfully) {
      showSnackbar({
        message: 'Tax identifier updated',
        severity: 'success',
      });
      closeDialog();
    } else {
      showSnackbar({
        message: 'Error updating tax identifier',
        severity: 'error',
      });
    }
  };

  return (
    <React.Fragment>
      <ButtonWithDialog
        componentType="Button"
        size="small"
        style={{ justifySelf: 'end', minWidth: 'fit-content', padding: '4px 10px' }}
        dialogTitle={`${taxDetails.tax_id ? 'Update' : 'Add'} tax identifier`}
        hideDialogActions
        onBeforeShow={() => reset({ tax_details: taxDetails })}
        dialogContent={(closeDialog) => (
          <form onSubmit={handleSubmit((data) => onFormSubmit(data, closeDialog))}>
            <Stack spacing={2} pt={1}>
              <TaxIdTextField
                control={control}
                name="tax_details.tax_id"
                label="TAX ID"
                placeholder={getTaxIdExample(country) || ''}
                fullWidth
                variant="outlined"
                required
                rules={{ ...VALIDATION_RULES.safeStringRequired, ...VALIDATION_RULES.taxId(country) }}
                taxId={taxId}
                country={country}
                onChangeTaxType={(newTaxType) =>
                  setValue('tax_details.tax_id_type', newTaxType || undefined, { shouldDirty: true })
                }
              />
              <Typography color="text.tertiary" style={{ marginTop: '8px' }}>
                Add a VAT or GST identification if you are paying as a business
              </Typography>
            </Stack>
            <Box pt={2}>
              <ContentModalActions
                onClose={closeDialog}
                confirmButtonText={`${taxDetails.tax_id ? 'Update' : 'Add'} tax identifier`}
                confirmButtonProps={{ loading: isUpdatingOrg, type: 'submit' }}
              />
            </Box>
          </form>
        )}
      >
        {taxDetails.tax_id ? 'Edit' : 'Add tax ID'}
      </ButtonWithDialog>
    </React.Fragment>
  );
};
